import React, { useState, useRef, useEffect, useMemo } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Table from "react-bootstrap/Table";
import Swal from "sweetalert2";
import Overlay from "react-bootstrap/Overlay";
import Tooltip from "react-bootstrap/Tooltip";

const TableWidget = (props) => {
  const { value, data } = useMemo(() => props, [props]);

  const formRef = useRef(null);
  const addRowFormRef = useRef(null);
  const columnInputRef = useRef(null);
  const tableInputRef = useRef(null);

  const [columnError, setColumnError] = useState("");

  const customColumns = [];
  let customRows = [];

  if (data?.options) {
    const rawOptions = data?.options?.trimEnd().split(",");
    rawOptions.forEach((element, index) => {
      const rawColumns = element?.trimEnd().split("=");
      if (rawColumns.length > 1) {
        customColumns.push(rawColumns[0]);
        const dataRow = rawColumns[1]?.trimEnd().split("|");
        const rowLenDeficite = customRows.length - dataRow.length;

        //add rows
        if (rowLenDeficite < 0) {
          for (let i = 0; i > rowLenDeficite; i--) {
            customRows.push([]);
          }
        }

        //populate rows
        dataRow.forEach((el2, index2) => {
          customRows[index2] = { ...customRows[index2], [rawColumns[0]]: el2 };
        });
      } else {
        customColumns.push(element);
      }
    });
  }

  const [defaultColumns, setDefaultColumns] = useState(customColumns);
  const [rows, setRows] = useState(
    Array.isArray(value) && value?.length > 0
      ? value
      : customRows.length > 0
      ? customRows
      : []
  );

  const defaultRows = rows;

  var addOnCols = [];
  if (defaultRows.length > 0) {
    addOnCols = Object.keys(defaultRows[defaultRows.length - 1]).filter(
      (col) => !defaultColumns.includes(col)
    );
  }

  const [columns, setColumns] = useState([...addOnCols]);

  const [showAddColumn, setShowAddColumn] = useState(false);
  const [showAddRow, setShowAddRow] = useState(false);

  const handleAddColumnClose = () => setShowAddColumn(false);
  const handleAddColumnShow = () => {
    setShowAddColumn(true);
  };

  const appendColumn = (column) => {
    if (column.trim()) {
      if (
        [...defaultColumns, ...columns].find(
          (col) => col.toUpperCase() === column.toUpperCase()
        )
      ) {
        setColumnError("Column name already exists");
      } else {
        setColumns([...columns, column]);
        columnInputRef.current.value = "";
        columnInputRef.current.focus();
        setColumnError("");
      }
    } else {
      setColumnError("Column name cannot be null");
    }
  };

  const appendRow = () => {
    const row = {};

    [...defaultColumns, ...columns].forEach((column) => {
      row[column] = addRowFormRef.current[column].value;
    });

    setRows([...rows, row]);
    addRowFormRef.current.reset();
  };

  const removeColumn = (columnIndex) => {
    setColumns([
      ...columns.filter((column, index) => {
        return index != columnIndex;
      }),
    ]);
  };

  const removeRow = (rowIndex) => {
    setRows([
      ...rows.filter((row, index) => {
        return index !== rowIndex;
      }),
    ]);
  };

  const handleClearRows = () => {
    Swal.fire({
      title: "Clear Records",
      text: "Do you want to continue",
      icon: "warning",
      allowOutsideClick: false,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        setRows([]);
      }
    });
  };

  const handleAddRowClose = () => {
    setShowAddRow(false);
  };
  const handleAddRowShow = () => setShowAddRow(true);

  const columnRow = [...defaultColumns, ...columns].map((column) => (
    <th key={column}>{column}</th>
  ));

  useEffect(() => {
    props?.onChange(rows);
    // console.log(rows);
  }, [rows]);

  const rowData =
    Array.isArray(rows) &&
    rows?.length > 0 &&
    rows?.map((row, index) => {
      return (
        <tr key={index}>
          {[...defaultColumns, ...columns].map((column) => (
            <td key={index + "-" + column}>
              {
                <TableWidgetInput
                  readOnly={props?.readOnly}
                  value={row[column]}
                  onCellUpdate={(value) => {
                    const updateRows = rows.slice();

                    row[column] = value;
                    updateRows.splice(index, 1, row);

                    setRows(updateRows);
                  }}
                />
              }
            </td>
          ))}
          <td>
            {!props?.readOnly && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeRow(index)}
              >
                <i className="bi bi-trash3" />
              </Button>
            )}
          </td>
        </tr>
      );
    });

  return (
    <>
      {!props?.readOnly && (
        <div className="mb-2">
          <Button
            size="sm"
            variant="outline-success"
            onClick={handleAddRowShow}
            title="Add Row"
            className="me-2"
          >
            <i className="bi bi-plus-circle fs-5"></i>
          </Button>
          <Button
            size="sm"
            variant="outline-info"
            onClick={handleAddColumnShow}
            title="Add Column"
            className="me-2"
          >
            <i className="bi bi-columns fs-5"></i>
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={handleClearRows}
            title="Clear rows"
            className="me-2"
          >
            <i className="bi bi-trash-fill fs-5"></i>
          </Button>
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-hover table-bordered">
          <thead>
            <tr>
              {columnRow}
              <th style={{ width: 25 }}></th>
            </tr>
          </thead>
          <tbody>{rowData}</tbody>
        </table>
      </div>
      <Modal
        show={showAddColumn}
        onHide={handleAddColumnClose}
        backdrop="static"
        keyboard={false}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Column</Modal.Title>
        </Modal.Header>
        <Form
          onSubmit={(e) => e.preventDefault()}
          ref={formRef}
          autoComplete="off"
        >
          <Modal.Body>
            <Row>
              {columns.length > 0 && (
                <Col>
                  <Table responsive striped bordered hover>
                    <tbody>
                      {columns.map((column, index) => (
                        <tr key={column}>
                          <td>
                            <div className="d-flex">
                              <div className="p-1 w-100">{column}</div>
                              <div className="p-1 flex-shrink-1">
                                <Button
                                  variant="outline-danger"
                                  onClick={() => removeColumn(index)}
                                >
                                  <i className="bi bi-trash" />
                                </Button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              )}
              <Col>
                <Form.Group className="mb-3" controlId="formBasicEmail">
                  <Form.Label>Column Name</Form.Label>
                  <Form.Control
                    type="text"
                    size="lg"
                    name="column"
                    className="border-primary"
                    ref={columnInputRef}
                    onChange={() => setColumnError("")}
                    required
                    autoFocus
                  />
                  <Form.Text className="text-danger">{columnError}</Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleAddColumnClose}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={() => appendColumn(formRef.current.column.value)}
            >
              Add
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Modal
        show={showAddRow}
        onHide={handleAddRowClose}
        backdrop="static"
        keyboard={false}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Row</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form
            onSubmit={(e) => e.preventDefault()}
            ref={addRowFormRef}
            autoComplete="off"
            className="row"
          >
            {[...defaultColumns, ...columns].map((column, index) =>
              index == 0 ? (
                <Form.Group key={column} className="md-2 col-6">
                  <Form.Label className="fs-5">{column}</Form.Label>
                  <Form.Control
                    type="text"
                    size="lg"
                    name={column}
                    className="fs-4 border-primary"
                    autoFocus
                  />
                </Form.Group>
              ) : (
                <Form.Group key={column} className="md-2 col-6">
                  <Form.Label className="fs-4">{column}</Form.Label>
                  <Form.Control
                    type="text"
                    size="lg"
                    name={column}
                    className="fs-4 border-primary"
                  />
                </Form.Group>
              )
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleAddRowClose}>
            Close
          </Button>
          <Button variant="primary" onClick={() => appendRow()}>
            Add
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const TableWidgetInput = ({
  readOnly = false,
  value = "",
  onCellUpdate = () => {},
}) => {
  const [showAddColumn, setShowAddColumn] = useState(false);
  const handleAddColumnClose = () => setShowAddColumn(false);
  const handleAddColumnShow = () => setShowAddColumn(true);
  const [cellValue, setCellValue] = useState(value);

  const handleCellUpdate = () => {
    onCellUpdate(cellValue);
    handleAddColumnClose();
  };

  const [show, setShow] = useState(false);
  const target = useRef(null);

  return (
    <>
      <div className="d-flex">
        <span className="flex-grow-1">{cellValue}</span>
        <div
          ref={target}
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
        >
          {!readOnly && (
            <div onClick={handleAddColumnShow} style={{ cursor: "pointer" }}>
              <i className="bi bi-three-dots text-info fw-bold"></i>
            </div>
          )}

          <Overlay target={target.current} show={show}>
            {(props) => (
              <Tooltip id="overlay-example" {...props}>
                Click to edit
              </Tooltip>
            )}
          </Overlay>
        </div>
      </div>
      <Modal
        show={showAddColumn}
        onHide={handleAddColumnClose}
        backdrop="static"
        keyboard={false}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Cell Value</Modal.Title>
        </Modal.Header>
        <Form
          onSubmit={(e) => e.preventDefault()}
          // ref={formRef}
          autoComplete="off"
        >
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Cell Value</Form.Label>
              <Form.Control
                type="text"
                autoFocus
                value={cellValue}
                onChange={(e) => setCellValue(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleAddColumnClose}>
              Close
            </Button>
            <Button variant="primary" onClick={handleCellUpdate}>
              Submit
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default TableWidget;
